import { Fragment, useEffect, useId, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { WorkOrderCheckoutLineKind, type RecipientSource, type WorkOrderDto } from '@signalvote/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  PhonePreviewAside,
  PhonePreviewButton,
  REGISTRATION_WAIT_DAYS,
  RecipientSourcePicker,
  TextComposer,
  disclaimerFromPaidForBy,
  formatCents,
  formatUnitCents,
  imageUploadErrorMessage,
  recipientUploadErrorMessage,
  recipientsFor,
  getWorkOrderScheduleError,
  timeZoneName,
  savedRecipientChoice,
  textSendProblem,
  useObjectUrl,
  type RecipientChoice,
  type TextSendContent,
} from '@/components/work-orders';
import {
  useAttachWorkOrderImage,
  useExpressLink,
  useUpdateWorkOrder,
  useUploadWorkOrderRecipients,
  useWorkOrderCheckoutPreview,
} from '@/hooks/queries';
import { getCampaignNowTimeString, getCampaignTodayString } from '@/lib/calendarUtils';
import { queryKeys } from '@/lib/queryKeys';
import { BuyListStep } from './ExpressBuyList';
import { ExpressStep, type ExpressStepProps } from './ExpressStep';
import { InvitedBy } from './InvitedBy';
import { PreviewNotification } from './PreviewNotification';
import { readDraftImage, saveDraftAfterSignup, useDraftImage, writeDraftImage } from './expressDraft';
import { sendsInsideRegistrationWait } from './expressProgress';
import { TemplateChips, useTemplateTyping } from './TemplateStarter';
import type { ExpressStepViewProps } from './expressStepViews';
import { useExpressTextSend } from './useExpressTextSend';

/** Each line the server can bill, in the customer's words, with the one-line why where a line needs one. */
export const CHECKOUT_LINE_COPY: Record<WorkOrderCheckoutLineKind, { label: (quantity: number) => string; why?: string }> = {
  [WorkOrderCheckoutLineKind.TextSend]: { label: (n) => `${n.toLocaleString('en-US')} ${n === 1 ? 'text' : 'texts'}` },
  [WorkOrderCheckoutLineKind.VoterData]: { label: () => 'Voter list' },
  [WorkOrderCheckoutLineKind.Registration]: {
    label: () => 'Carrier registration (one-time)',
    why: 'Carriers require it once, before your first text.',
  },
};

/** What the server would bill the Draft now (its checkout preview), or null until it has priced something. */
function usePricedOrder(workOrderId: string) {
  const preview = useWorkOrderCheckoutPreview(workOrderId).data;
  return preview && preview.totalCents > 0 ? preview : null;
}

/** The order bar's lines row: each label with its amount, and the one-line why under them. */
function BarLines({ rows, why }: { rows: { label: string; amount: string }[]; why?: string }) {
  return (
    <div className="text-xs text-muted-foreground">
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 tabular-nums">
        {rows.map((row) => (
          <Fragment key={row.label}>
            <dt>{row.label}</dt>
            <dd className="text-right">{row.amount}</dd>
          </Fragment>
        ))}
      </dl>
      {why && <p className="mt-1 text-[11px] leading-snug">{why}</p>}
    </div>
  );
}

/**
 * The order bar's lines: every line the server prices, with the one-line why where a line has one, so
 * a first send shows its one-time registration from the first priced screen.
 */
function OrderLines({ workOrderId }: { workOrderId: string }) {
  const order = usePricedOrder(workOrderId);
  if (!order) return null;
  const lines = order.lines.filter((line) => line.quantity > 0);
  return (
    <BarLines
      rows={lines.map((line) => ({ label: CHECKOUT_LINE_COPY[line.kind].label(line.quantity), amount: formatCents(line.amountCents) }))}
      why={lines.map((line) => CHECKOUT_LINE_COPY[line.kind].why).find((text) => text !== undefined)}
    />
  );
}

/**
 * What a text costs here, before any account or order exists: each text, and the one-time carrier
 * registration, as the server reads them from Stripe. Nothing shows until the server answers, so no
 * number on the page is ever the web's guess.
 */
function PriceList({ code }: { code: string }) {
  const prices = useExpressLink(code).data;
  const registration = CHECKOUT_LINE_COPY[WorkOrderCheckoutLineKind.Registration];
  return (
    <div className="min-h-9 text-xs text-muted-foreground">
      {prices && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
          <dl className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <div className="flex items-baseline gap-1.5">
              <dt>Each text</dt>
              <dd className="text-sm font-semibold tabular-nums text-foreground">{formatUnitCents(prices.textSendUnitAmountCents)}</dd>
            </div>
            <div className="flex items-baseline gap-1.5">
              <dt>{registration.label(1)}</dt>
              <dd className="text-sm font-semibold tabular-nums text-foreground">{formatCents(prices.registrationAmountCents)}</dd>
            </div>
          </dl>
          <p className="mt-1 text-[11px] leading-snug">{`${registration.why} Nothing is charged until you pay.`}</p>
        </motion.div>
      )}
    </div>
  );
}

/**
 * The chrome of the Express link's steps before the account exists: who sent the link, and in the order
 * bar what a text costs, with no total yet.
 */
export function expressLinkChrome(code: string): Pick<ExpressStepProps, 'lines' | 'invitedBy'> {
  return { lines: <PriceList code={code} />, invitedBy: <InvitedBy code={code} /> };
}

/** The order bar's running total, beside its button. */
function OrderTotal({ workOrderId }: { workOrderId: string }) {
  const order = usePricedOrder(workOrderId);
  if (!order) return null;
  return (
    <span data-testid="order-total" className="text-lg font-semibold tabular-nums text-foreground">
      {formatCents(order.totalCents)}
    </span>
  );
}

/** The order bar's pieces for a Draft: its lines on their own row, its total beside the button. */
export function orderBar(workOrderId: string): Pick<ExpressStepProps, 'summary' | 'lines'> {
  return { summary: <OrderTotal workOrderId={workOrderId} />, lines: <OrderLines workOrderId={workOrderId} /> };
}

function contentOf(workOrder: WorkOrderDto): TextSendContent {
  return {
    body: workOrder.details.messageText ?? '',
    paidForDisclaimer: workOrder.details.paidForDisclaimer ?? '',
    imageUrl: workOrder.details.imagePreviewUrl,
  };
}

function Waiting({ step, onBack, failed }: Pick<ExpressStepViewProps, 'step' | 'onBack'> & { failed: boolean }) {
  return (
    <ExpressStep step={step} onBack={onBack} error={failed ? 'We couldn’t load your text. Refresh to try again.' : null}>
      {!failed && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading your text…
        </p>
      )}
    </ExpressStep>
  );
}

function MissingText({ step, onBack }: Pick<ExpressStepViewProps, 'step' | 'onBack'>) {
  return (
    <ExpressStep step={step} onBack={onBack} error="We couldn’t find your text. Go back and write it again.">
      {null}
    </ExpressStep>
  );
}

/**
 * Write your text. Before the account exists, and for a campaign with no Draft yet, the text waits on
 * this device; once a Draft exists, it edits the Draft.
 */
export function MessageStep(props: ExpressStepViewProps) {
  return props.campaign ? <CampaignMessage {...props} /> : <DeviceMessage {...props} />;
}

function CampaignMessage(props: ExpressStepViewProps) {
  const send = useExpressTextSend();
  if (send.isLoading || send.isError) return <Waiting step={props.step} onBack={props.onBack} failed={send.isError} />;
  return send.workOrder ? <SavedMessage {...props} workOrder={send.workOrder} /> : <DeviceMessage {...props} />;
}

function DeviceMessage({ step, draft, setDraft, campaign, code, onBack, onNext }: ExpressStepViewProps) {
  const queryClient = useQueryClient();
  const { image, setImage } = useDraftImage();
  const imageUrl = useObjectUrl(image);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const body = draft.message?.body ?? '';
  const paidForDisclaimer = draft.message?.paidForDisclaimer ?? disclaimerFromPaidForBy(campaign?.paidForBy);
  const content = { body, paidForDisclaimer, imageUrl };
  const edit = (patch: { body?: string; paidForDisclaimer?: string }) =>
    setDraft((d) => ({ ...d, message: { body: d.message?.body ?? '', paidForDisclaimer: d.message?.paidForDisclaimer, ...patch } }));
  const templates = useTemplateTyping(body, (value) => edit({ body: value }));

  // With a campaign, Continue saves the text there, the same hand-off sign-up makes.
  const next = async () => {
    if (!campaign) return onNext?.();
    setSaving(true);
    setError(null);
    const unsaved = await saveDraftAfterSignup({ ...draft, message: { body, paidForDisclaimer } }, campaign.id).catch(
      () => 'failed' as const,
    );
    setSaving(false);
    await queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    if (unsaved === null) onNext?.();
    else setError(unsaved === 'failed' ? 'We couldn’t save your text. Try again.' : 'Your text is saved, but the image didn’t upload. Try again.');
  };

  return (
    <ExpressStep
      step={step}
      onBack={onBack}
      error={error}
      preview={<PhonePreviewAside {...content} size="large" typing={templates.typing} />}
      {...(code === undefined ? {} : expressLinkChrome(code))}
      primary={{
        label: 'Continue',
        disabled: textSendProblem(content) !== null,
        busy: saving,
        busyLabel: 'Saving…',
        onPress: () => void next(),
      }}
    >
      <PreviewNotification {...content} />
      <TemplateChips {...templates} />
      <TextComposer
        autoFocus
        body={body}
        paidForDisclaimer={paidForDisclaimer}
        imageUrl={imageUrl}
        onBodyChange={(value) => edit({ body: value })}
        onBodyInterrupt={templates.stop}
        onDisclaimerChange={(value) => edit({ paidForDisclaimer: value })}
        onImagePick={setImage}
        onImageRemove={() => setImage(null)}
      />
    </ExpressStep>
  );
}

function SavedMessage({ step, workOrder, onBack, onNext }: ExpressStepViewProps & { workOrder: WorkOrderDto }) {
  const [body, setBody] = useState<string | null>(null);
  const [disclaimer, setDisclaimer] = useState<string | null>(null);
  const { mutate: update, isPending: saving, error: saveError } = useUpdateWorkOrder();
  const { mutate: attachImage, isPending: attaching, error: imageError } = useAttachWorkOrderImage();
  const saved = contentOf(workOrder);
  const content = { ...saved, body: body ?? saved.body, paidForDisclaimer: disclaimer ?? saved.paidForDisclaimer };
  const dirty = content.body !== saved.body || content.paidForDisclaimer !== saved.paidForDisclaimer;
  const problem = textSendProblem(content);
  const templates = useTemplateTyping(content.body, setBody);

  // An image that didn't upload at sign-up is still on this device: upload it now.
  useEffect(() => {
    void readDraftImage().then((file) => {
      if (file)
        attachImage(
          { id: workOrder.id, file, expectedConcurrencyVersion: workOrder.concurrencyVersion },
          { onSuccess: () => void writeDraftImage(null) },
        );
    });
    // Only on arrival: the upload itself bumps the token, which must not re-run it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachImage, workOrder.id]);

  const save = (then?: () => void) =>
    update(
      {
        id: workOrder.id,
        body: { messageText: content.body, paidForDisclaimer: content.paidForDisclaimer, sendDate: null, sendTime: null, amount: null },
      },
      { onSuccess: () => then?.() },
    );

  return (
    <ExpressStep
      step={step}
      onBack={onBack}
      error={saveError ? 'We couldn’t save your text. Try again.' : null}
      preview={<PhonePreviewAside {...content} size="large" typing={templates.typing} />}
      primary={{
        label: 'Continue',
        disabled: problem !== null,
        busy: saving,
        busyLabel: 'Saving…',
        onPress: () => (dirty ? save(onNext) : onNext?.()),
      }}
    >
      <PreviewNotification {...content} />
      <TemplateChips {...templates} />
      <TextComposer
        body={content.body}
        paidForDisclaimer={content.paidForDisclaimer}
        imageUrl={content.imageUrl}
        onBodyChange={setBody}
        onBodyInterrupt={templates.stop}
        onDisclaimerChange={setDisclaimer}
        onBlur={() => dirty && problem === null && save()}
        onImagePick={(file) => attachImage({ id: workOrder.id, file, expectedConcurrencyVersion: workOrder.concurrencyVersion })}
        imageBusy={attaching}
        imageError={imageUploadErrorMessage(imageError)}
      />
    </ExpressStep>
  );
}

/** Who gets it: an uploaded list, the campaign's voters with a cell phone, or a list to buy. */
export function RecipientsStep({ step, onBack, onNext }: ExpressStepViewProps) {
  const send = useExpressTextSend();
  const [choice, setChoice] = useState<RecipientChoice | null>(null);
  const { mutate: update } = useUpdateWorkOrder();
  const { mutate: upload, isPending: uploading, error: uploadError } = useUploadWorkOrderRecipients();

  if (send.isLoading || send.isError) return <Waiting step={step} onBack={onBack} failed={send.isError} />;
  const workOrder = send.workOrder;
  if (!workOrder) return <MissingText step={step} onBack={onBack} />;

  // The saved Draft seeds the choice once; after that it is the person's, so taking a bought list out
  // of the order leaves them on Buy a list to add another.
  const saved = savedRecipientChoice(workOrder);
  if (choice === null && saved !== null) setChoice(saved);
  const picked = choice ?? saved;
  const count = recipientsFor(workOrder, picked);
  const content = contentOf(workOrder);
  const setSource = (recipientSource: RecipientSource) =>
    update({ id: workOrder.id, body: { messageText: null, sendDate: null, sendTime: null, amount: null, recipientSource } });
  const frame = { step, onBack, aside: <PhonePreviewAside {...content} /> };
  const picker = (buy: ReactNode) => (
    <RecipientSourcePicker
      workOrder={workOrder}
      choice={picked}
      onChoiceChange={setChoice}
      onSourceChange={setSource}
      onUpload={(file) => upload({ id: workOrder.id, file, expectedConcurrencyVersion: workOrder.concurrencyVersion })}
      uploading={uploading}
      uploadError={recipientUploadErrorMessage(uploadError)}
      buy={buy}
    />
  );
  // The list step previews the list; every other choice previews the text.
  if (picked === 'buy') {
    return <BuyListStep workOrder={workOrder} frame={frame} picker={picker} onNext={onNext} />;
  }
  return (
    <ExpressStep {...frame} {...orderBar(workOrder.id)} primary={{ label: 'Continue', disabled: !count, onPress: () => onNext?.() }}>
      {picker(null)}
      <div className="mt-6">
        <PhonePreviewButton {...content} />
      </div>
    </ExpressStep>
  );
}

/** When it goes out: a date and time in the campaign's time zone, saved to the Draft. */
export function ScheduleStep({ step, campaign, onBack, onNext }: ExpressStepViewProps) {
  const id = useId();
  const send = useExpressTextSend();
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const { mutate: update, isPending: saving, error: saveError } = useUpdateWorkOrder();

  if (send.isLoading || send.isError) return <Waiting step={step} onBack={onBack} failed={send.isError} />;
  const workOrder = send.workOrder;
  if (!workOrder) return <MissingText step={step} onBack={onBack} />;

  const sendDate = date ?? workOrder.sendDate ?? '';
  const sendTime = time ?? workOrder.sendTime ?? '';
  const today = getCampaignTodayString();
  const pastError = getWorkOrderScheduleError(sendDate, sendTime, today, getCampaignNowTimeString());
  const complete = sendDate !== '' && sendTime !== '' && pastError === null;
  const dirty = sendDate !== (workOrder.sendDate ?? '') || sendTime !== (workOrder.sendTime ?? '');
  const save = (then?: () => void) =>
    update(
      { id: workOrder.id, body: { messageText: null, sendDate: sendDate || null, sendTime: sendTime || null, amount: null } },
      { onSuccess: () => then?.() },
    );
  const zone = timeZoneName(campaign?.timeZoneId);

  return (
    <ExpressStep
      step={step}
      onBack={onBack}
      error={saveError ? 'We couldn’t save the date. Try again.' : null}
      aside={<PhonePreviewAside {...contentOf(workOrder)} />}
      {...orderBar(workOrder.id)}
      primary={{
        label: 'Continue',
        disabled: !complete,
        busy: saving,
        busyLabel: 'Saving…',
        onPress: () => (dirty ? save(onNext) : onNext?.()),
      }}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-date`}>Send date</Label>
            <Input
              id={`${id}-date`}
              type="date"
              min={today}
              value={sendDate}
              onChange={(e) => setDate(e.target.value)}
              onBlur={() => dirty && pastError === null && save()}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-time`}>Send time</Label>
            <Input
              id={`${id}-time`}
              type="time"
              value={sendTime}
              onChange={(e) => setTime(e.target.value)}
              onBlur={() => dirty && pastError === null && save()}
            />
          </div>
        </div>
        {zone && <p className="text-sm text-muted-foreground">Your text goes out at this time, {zone}.</p>}
        {pastError && (
          <p role="alert" className="text-sm text-destructive">
            {pastError}
          </p>
        )}
        {workOrder.isFirstSend &&
          (sendsInsideRegistrationWait(sendDate, today) ? (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <p>
                Carrier registration takes about {REGISTRATION_WAIT_DAYS} days. Your text goes out on this date only if
                registration has cleared by then. Otherwise it goes out as soon as it does, and we’ll email you.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Your first text waits for carrier registration, about {REGISTRATION_WAIT_DAYS} days. We’ll email you when
              it clears.
            </p>
          ))}
        <PhonePreviewButton {...contentOf(workOrder)} />
      </div>
    </ExpressStep>
  );
}
